/**
 * Singbac Incorporation (Pre) — 문서별 [[Signers]] 컨텍스트.
 *
 * PRE 3종(Form 45 이사 동의 · Form 45B 비서 동의 · Notice to Controller)은 각기
 * 부수(scope: dir_{id}/ · sec_{id}/ · ctrl_{id}/)마다 서명자가 다르므로, 서명 키를
 * 스코프별(`dir_1/Signers` 등)로 저장한다. Pre 단계는 L_ENTITY 가 없어 officer API 를
 * 못 쓰므로 본인 성명 seed + 수동 추가만 사용한다(Aura/POST Pre 패턴과 동일).
 */
import { inject, provide, reactive, ref, type InjectionKey } from 'vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

export type SgIncPreSignersCtx = {
  signersOf: (varKey: string) => SignerEntry[];
  setSigners: (varKey: string, list: SignerEntry[]) => void;
  /**
   * 본인 성명 → 서명자 1명. 비어 있으면 seed, 자동으로 만든 1명(`${varKey}-self`)뿐이면 성명을 따라간다.
   * 사용자가 서명자를 바꿨으면(다른 id·여러 명) 덮지 않음.
   */
  seedIfEmpty: (varKey: string, name: string, title: string) => void;
  exportAll: () => Record<string, SignerEntry[]>;
  importAll: (map: Record<string, SignerEntry[]> | null | undefined) => void;
  /** importAll(복원) 횟수 — 복원이 store 를 비운 뒤 서명 칸이 성명으로 다시 seed 하도록 watch 에 쓴다 */
  revision: () => number;
};

export const SG_INC_PRE_SIGNERS: InjectionKey<SgIncPreSignersCtx> = Symbol('singbacIncPreSigners');

/** 성명 1명 → 수동 서명자 엔트리(박스형, T-Sign). */
export function buildOneSigner(id: string, name: string, title: string): SignerEntry[] {
  const nm = String(name ?? '').trim();
  if (!nm) return [];
  return [
    {
      id,
      name: nm,
      title,
      role: 'director',
      isManual: true,
      showTitle: true,
      _signerLayout: 'box',
    } as SignerEntry,
  ];
}

export function provideSgIncPreSigners(): SgIncPreSignersCtx {
  const store = reactive<Record<string, SignerEntry[]>>({});
  const rev = ref(0);

  const ctx: SgIncPreSignersCtx = {
    signersOf: (varKey) => store[varKey] || [],
    setSigners: (varKey, list) => {
      store[varKey] = Array.isArray(list) ? list.map((s) => ({ ...s })) : [];
    },
    seedIfEmpty: (varKey, name, title) => {
      const selfId = `${varKey}-self`;
      const cur = store[varKey] || [];
      const nm = String(name ?? '').trim();
      if (!cur.length) {
        const seeded = buildOneSigner(selfId, nm, title);
        if (seeded.length) store[varKey] = seeded;
        return;
      }
      // 자동 seed 1명만 있으면 성명 입력을 따라간다 — 예전엔 첫 글자 입력 때 seed 되고 멈춰 'T' 만 남았다.
      if (cur.length === 1 && cur[0]?.id === selfId) {
        if (!nm) { store[varKey] = []; return; }
        if (cur[0].name !== nm) store[varKey] = [{ ...cur[0], name: nm }];
      }
    },
    exportAll: () => {
      const out: Record<string, SignerEntry[]> = {};
      for (const [k, v] of Object.entries(store)) {
        if (v?.length) out[k] = v.map((s) => ({ ...s }));
      }
      return out;
    },
    importAll: (map) => {
      for (const k of Object.keys(store)) delete store[k];
      if (map && typeof map === 'object') {
        for (const [k, v] of Object.entries(map)) {
          if (Array.isArray(v)) store[k] = v.map((s) => ({ ...s }));
        }
      }
      rev.value += 1;
    },
    revision: () => rev.value,
  };

  provide(SG_INC_PRE_SIGNERS, ctx);
  return ctx;
}

export function useSgIncPreSigners(): SgIncPreSignersCtx {
  const ctx = inject(SG_INC_PRE_SIGNERS, null);
  if (!ctx) {
    return {
      signersOf: () => [],
      setSigners: () => {},
      seedIfEmpty: () => {},
      exportAll: () => ({}),
      importAll: () => {},
      revision: () => 0,
    };
  }
  return ctx;
}
